import { apiFetch } from '@wundo/client/api.ts';

/**
 * What a profile picture may be, mirrored from the backend
 * (`utils/profileImage.ts`). Narrower than the images the app accepts
 * elsewhere: these bytes are served back under a permanent cache header.
 */
export const PROFILE_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

/**
 * The cap the upload route enforces (`MAX_PROFILE_IMAGE_BYTES`), mirrored rather than
 * shared: the backend refuses anything larger whatever the browser believes,
 * and knowing the number here is what lets the page say so before spending a
 * megabyte on a request that ends in a 422.
 */
export const MAX_PROFILE_IMAGE_BYTES = 1024 * 1024;

/**
 * Replace the person's picture. Multipart, field `file`, with the content type
 * left to the browser so the boundary is right.
 *
 * Answers 204 with no body, and the URL is versioned — so the new picture only
 * exists for the app once the user has been fetched again.
 */
export async function uploadProfileImage(userId: string, file: File): Promise<void> {
  const body = new FormData();
  body.append('file', file);

  await apiFetch(`/users/${userId}/profile-image`, { method: 'PUT', body });
}

/** Drop the picture. 204 either way, including when there was none. */
export async function removeProfileImage(userId: string): Promise<void> {
  await apiFetch(`/users/${userId}/profile-image`, { method: 'DELETE' });
}
